import { useAuth } from "@/contexts/AuthContext";
import { Link } from "react-router-dom";
import { Lock } from "lucide-react";

export const FEATURE_LABELS = {
  "os.budget_edit": "Edição de orçamento", "os.checklist": "Checklist técnico", "os.videos": "Vídeos da OS",
  "os.inspection": "Vistoria com assinatura", "os.photos": "Fotos", "os.profit": "Rentabilidade na OS",
  "os.payments": "Pagamentos", "os.rework": "Retrabalho",
  "clientes": "Clientes",
  "estoque.purchases": "Compras", "estoque.suppliers": "Fornecedores", "estoque.counter_sale": "Venda balcão",
  "financeiro.conciliacao": "Conciliação bancária", "financeiro.payment_machines": "Maquininhas",
  "crm.satisfacao": "Satisfação", "crm.posvenda": "Pós-venda", "crm.lembretes": "Lembretes",
  "crm.funil": "Funil de origem", "crm.clube": "Clube de pontos",
  "patio.tv": "Modo TV do pátio",
  "relatorios.rentabilidade": "Rentabilidade (relatório)", "relatorios.equipe": "Produtividade da equipe",
  "relatorios.dre": "DRE, balancete e centros de custo", "estoque.sugestao": "Sugestão de compra",
  "relatorios.pneus": "Relatório de venda de pneus", "relatorios.margem_baixa": "Alerta de margem baixa (relatório e cartão na Central)",
  "relatorios.painel": "Painel de indicadores",
  "os.garantia": "Consulta e aviso de garantia na Nova OS",
  "fiscal.relatorio_contador": "Relatório para o contador (XLSX + XMLs)", "fiscal.cancelar": "Cancelar nota pela tela",
  "fiscal.cupom_termico": "Imprimir cupom térmico", "fiscal.sped": "SPED Fiscal (EFD ICMS/IPI)",
};

export const FEATURE_PARENT = {
  "os.budget_edit": "os", "os.checklist": "checklist", "os.videos": "os", "os.inspection": "os", "os.photos": "os",
  "os.profit": "os", "os.payments": "financeiro", "os.rework": "os", "clientes": "clientes",
  "estoque.purchases": "estoque", "estoque.suppliers": "estoque", "estoque.counter_sale": "estoque",
  "financeiro.conciliacao": "financeiro", "financeiro.payment_machines": "financeiro",
  "crm.satisfacao": "crm", "crm.posvenda": "crm", "crm.lembretes": "crm", "crm.funil": "crm", "crm.clube": "crm",
  "patio.tv": "patio", "relatorios.rentabilidade": "relatorios", "relatorios.equipe": "relatorios",
  "relatorios.dre": "financeiro", "estoque.sugestao": "estoque", "relatorios.pneus": "relatorios", "relatorios.margem_baixa": "relatorios", "relatorios.painel": "relatorios",
  "os.garantia": "os", "fiscal.relatorio_contador": "fiscal", "fiscal.cancelar": "fiscal", "fiscal.cupom_termico": "fiscal", "fiscal.sped": "fiscal",
};

export const FEATURE_BLOCK_LABEL = { os: "Dentro da OS", checklist: "Checklist técnico", clientes: "Clientes", estoque: "Estoque", financeiro: "Financeiro", crm: "CRM", patio: "Pátio", relatorios: "Relatórios", fiscal: "Fiscal" };

export const ROUTE_FEATURE = [
  [/^\/owner\/compras/, "estoque.purchases"], [/^\/owner\/fornecedores/, "estoque.suppliers"],
  [/^\/owner\/balcao/, "estoque.counter_sale"], [/^\/owner\/conciliar/, "financeiro.conciliacao"],
  [/^\/owner\/satisfacao/, "crm.satisfacao"], [/^\/owner\/pos-venda/, "crm.posvenda"],
  [/^\/owner\/lembretes/, "crm.lembretes"], [/^\/owner\/funil/, "crm.funil"], [/^\/owner\/clube/, "crm.clube"],
  [/^\/owner\/rentabilidade/, "relatorios.rentabilidade"], [/^\/owner\/equipe/, "relatorios.equipe"],
  [/^\/owner\/dre/, "relatorios.dre"], [/^\/owner\/sugestao-compra/, "estoque.sugestao"], [/^\/owner\/pneus/, "relatorios.pneus"],
  [/^\/owner\/margem-baixa/, "relatorios.margem_baixa"], [/^\/owner\/painel/, "relatorios.painel"],
  [/^\/owner\/calculadora-precos/, "financeiro.calculadora"],
];

export function featureForRoute(path) {
  for (const [re, f] of ROUTE_FEATURE) { if (re.test(path)) return f; }
  return null;
}

export function useFeatures() {
  const { user } = useAuth();
  const map = user?.shop?.features || {};
  const planBlocked = new Set(map._plan_blocked || []);
  const hidden = new Set(map._hidden || []);
  return {
    enabled: (k) => map[k] !== false,
    planBlocked: (k) => planBlocked.has(k),
    hidden: (k) => hidden.has(k),
    all: map,
  };
}

export function FeatureGate({ feature, children, fallback = null }) {
  const { enabled } = useFeatures();
  return enabled(feature) ? children : fallback;
}

export function FeatureLocked({ feature }) {
  return (
    <div data-testid={`feature-locked-${feature}`} className="rounded-xl border border-amber-800/60 bg-amber-950/20 p-3 text-sm text-amber-200 flex items-center gap-2">
      <Lock className="h-4 w-4" /> <span>{FEATURE_LABELS[feature] || feature} está no outro plano.</span>
      <Link to="/owner/meu-plano" className="ml-auto font-bold text-amber-300 underline">Ver planos</Link>
    </div>
  );
}
